// assets/edm-bundle-mobile-manager.js
// Mobile Manager - Handles sticky bar, bottom sheet, and mobile-specific UI
// Context7: Manager Pattern - Separates mobile concerns from Controller
// AUDIT FIX: Implements AbortController pattern, observer cleanup, requestIdleCallback fallback

window.EdumonBundle = window.EdumonBundle || {};

window.EdumonBundle.MobileManager = {
  /**
   * Initialize mobile manager
   * @param {Object} config - Configuration
   * @param {Object} config.state - State instance (by reference)
   * @param {Array} config.tiers - Discount tiers
   * @param {number} config.maxSlots - Maximum slots
   * @param {Element} config.sheet - Sheet element
   * @param {Element} config.stickyEl - Sticky bar element
   * @param {Element} config.scrimGlobal - Scrim element
   * @param {number} config.productId - Product ID
   * @param {number} config.openCartAfterAdd - Whether to open cart after add
   * @param {number} config.FAST_OPEN_MS - Fast open threshold
   * @param {Function} config.updateUI - Update UI function
   * @param {Function} config.markDirty - Mark dirty function
   * @param {Function} config.syncCartBundleToState - Sync cart function
   * @param {Function} config.loadBundleFromCart - Load bundle function
   * @param {Function} config.getUserClosedDuringSync - Get user closed during sync function
   * @returns {Object} Manager instance
   */
  init: function(config) {
    const Config = window.EdumonBundle.Config;
    const Utils = window.EdumonBundle.Utils;
    const UIRenderMobile = window.EdumonBundle.UIRenderMobile;
    const Analytics = window.EdumonBundle.Analytics;
    const API = window.EdumonBundle.API;
    
    const {
      state,
      tiers,
      maxSlots,
      sheet,
      stickyEl,
      scrimGlobal,
      productId,
      openCartAfterAdd,
      FAST_OPEN_MS,
      updateUI,
      markDirty,
      syncCartBundleToState,
      loadBundleFromCart,
      getUserClosedDuringSync
    } = config;
    
    // AUDIT FIX: Create AbortController for all event listeners
    const abortController = new AbortController();
    const signal = abortController.signal;
    
    // AUDIT FIX: Store observers for cleanup
    let ioAnchor = null;
    let mqMobile = null;
    let mqMobileListener = null;
    
    // State
    let sheetOpen = false;
    let sheetPrevFocus = null;
    let sheetItems = null;
    let scrollY = 0;
    let lastY = window.pageYOffset || document.documentElement.scrollTop || 0;
    let scrollDir = 'down';
    let hasSeenAnchor = false;
    let anchorInView = true;
    let anchorDocTop = 0;
    let observedBtn = null;
    let bbDebounceTimer = null;
    
    // Elements
    const panel = sheet?.querySelector(Config.SELECTORS.SHEET_PANEL);
    const productForms = Array.from(document.querySelectorAll(Config.SELECTORS.PRODUCT_FORM));
    const productForm = productForms.find(f => f.querySelector(Config.SELECTORS.ATC_BUTTON)) || productForms[0];
    const mobileCTA = document.querySelector(Config.SELECTORS.OPEN_PICKER);
    const ctaBtn = document.querySelector(Config.SELECTORS.OPEN_PICKER);
    
    // Helper: Find main ATC button
    const findMainATC = (root) => {
      return root && (root.querySelector('button[name="add"]') || root.querySelector('[type="submit"]'));
    };
    let mainATC = findMainATC(productForm);
    let anchorEl = mainATC || mobileCTA;
    
    // Mobile Helper Functions
    const mobile_buildTicks = () => {
      if (!Utils.isMobile() || !stickyEl) return;
      UIRenderMobile.buildTicks(stickyEl, tiers, maxSlots);
    };
    
    const mobile_computeShortSummary = () => {
      const q = state.totalQty;
      let nextTier = tiers.find(t => t.qty > q);
      let currentTier = null;
      for (const t of tiers) if (q >= t.qty) currentTier = t;
      if (q === 0) return '0/10 Booster • ab 3 −10 %';
      if (!currentTier && nextTier) return `${q}/10 Booster • noch ${nextTier.qty-q} bis −${nextTier.off} %`;
      if (currentTier && nextTier) return `${q}/10 Booster • −${currentTier.off} % aktiv • noch ${nextTier.qty-q} bis −${nextTier.off} %`;
      if (currentTier && !nextTier) return `${q}/10 Booster • −${currentTier.off} % aktiv`;
      return `${q}/10 Booster`;
    };
    
    const mobile_updateSticky = () => {
      if (!Utils.isMobile() || !stickyEl) return;
      UIRenderMobile.updateStickyBar(stickyEl, state, tiers, maxSlots);
    };
    
    // Sticky Gate (Scroll-Direction)
    const openFromSticky = stickyEl?.querySelector(Config.SELECTORS.OPEN);
    if (stickyEl && stickyEl.parentNode !== document.body) document.body.appendChild(stickyEl);
    if (stickyEl) {
      stickyEl.removeAttribute('hidden');
      stickyEl.setAttribute('aria-hidden', 'true');
    }
    
    // Header-Bottom-Messung
    const bbMeasureHeaderBottom = () => {
      UIRenderMobile.measureHeaderBottom();
    };
    const bbScheduleMeasure = () => {
      UIRenderMobile.scheduleMeasureHeader();
    };
    bbMeasureHeaderBottom();
    
    // AUDIT FIX: Use AbortController for event listeners
    window.addEventListener('resize', bbScheduleMeasure, { passive: true, signal });
    window.addEventListener('scroll', bbScheduleMeasure, { passive: true, signal });
    document.addEventListener('shopify:section:load', bbScheduleMeasure, { signal });
    document.addEventListener('shopify:section:reorder', bbScheduleMeasure, { signal });
    
    const bbShow = () => {
      if (!stickyEl) return;
      stickyEl.classList.add(Config.CLASSES.VISIBLE);
      stickyEl.setAttribute('aria-hidden', 'false');
    };
    const bbHide = () => {
      if (!stickyEl) return;
      stickyEl.classList.remove(Config.CLASSES.VISIBLE);
      stickyEl.setAttribute('aria-hidden', 'true');
    };
    
    const computeDocTop = (el) => {
      const r = el.getBoundingClientRect();
      const scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
      return r.top + scrollY;
    };
    
    // AUDIT FIX: Use AbortController for scroll listener
    window.addEventListener('scroll', () => {
      const y = window.pageYOffset || document.documentElement.scrollTop || 0;
      const nextDir = (y > lastY) ? 'down' : 'up';
      if (nextDir !== scrollDir) {
        scrollDir = nextDir;
        bbDebouncedUpdate();
      }
      lastY = y;
      
      const viewportBottom = y + (window.innerHeight || document.documentElement.clientHeight);
      if (viewportBottom < (anchorDocTop - 8)) {
        hasSeenAnchor = false;
        anchorInView = false;
        bbHide();
      }
    }, { passive: true, signal });
    
    // Debounce
    const bbDebouncedUpdate = () => {
      if (bbDebounceTimer) clearTimeout(bbDebounceTimer);
      bbDebounceTimer = setTimeout(bbUpdateVisibility, Config.CONSTANTS.DEBOUNCE_DELAY_MS);
    };
    
    const bbUpdateVisibility = () => {
      const sheetOpenCheck = document.documentElement.classList.contains(Config.CLASSES.SHEET_OPEN);
      const shouldShow = hasSeenAnchor && scrollDir === 'down' && !anchorInView && !sheetOpenCheck;
      if (shouldShow) bbShow();
      else bbHide();
    };
    
    // AUDIT FIX: IntersectionObserver with cleanup
    const bbBuildObserver = () => {
      if (ioAnchor) {
        try { ioAnchor.disconnect(); } catch(e) {}
        ioAnchor = null;
      }
      
      if (!('IntersectionObserver' in window)) {
        const r = anchorEl.getBoundingClientRect();
        anchorInView = r.bottom > 0 && r.top < (window.innerHeight || document.documentElement.clientHeight);
        if (anchorInView) hasSeenAnchor = true;
        bbDebouncedUpdate();
        return;
      }
      
      const margin = `0px 0px -8px 0px`;
      ioAnchor = new IntersectionObserver((entries) => {
        const entry = entries[0];
        const inView = !!(entry && (entry.isIntersecting || entry.intersectionRatio > 0));
        anchorInView = inView;
        if (inView) hasSeenAnchor = true;
        bbDebouncedUpdate();
      }, { root: null, threshold: 0, rootMargin: margin });
      
      bbAttachObserver();
    };
    
    const bbAttachObserver = () => {
      if (!ioAnchor) return;
      const freshATC = findMainATC(productForm) || mainATC;
      const isMobileLayout = Utils.isMobile();
      if (isMobileLayout && mobileCTA) {
        anchorEl = mobileCTA;
      } else if (freshATC) {
        anchorEl = freshATC;
      }
      if (!anchorEl) return;
      if (observedBtn) ioAnchor.unobserve(observedBtn);
      observedBtn = anchorEl;
      ioAnchor.observe(observedBtn);
      const r = observedBtn.getBoundingClientRect();
      anchorInView = r.bottom > 0 && r.top < (window.innerHeight || document.documentElement.clientHeight);
      if (anchorInView) hasSeenAnchor = true;
      anchorDocTop = computeDocTop(observedBtn);
      bbDebouncedUpdate();
    };
    
    bbBuildObserver();
    
    // AUDIT FIX: Use AbortController for resize listener
    window.addEventListener('resize', () => {
      if (observedBtn) anchorDocTop = computeDocTop(observedBtn);
      bbBuildObserver();
    }, { signal });
    
    const mobile_gateSticky = () => {
      bbBuildObserver();
      bbDebouncedUpdate();
    };
    
    // AUDIT FIX: Use AbortController for sticky CTA click
    openFromSticky?.addEventListener('click', (e) => {
      e.preventDefault();
      const isMobileLayout = Utils.isMobile();
      if (isMobileLayout) {
        const openBtn = document.querySelector(Config.SELECTORS.OPEN_PICKER);
        if (openBtn) openBtn.click();
      } else {
        document.getElementById(Config.SELECTORS.BUNDLE_BUILDER.replace('#', ''))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, { signal });
    
    // Elemente ggf. direkt unter <body> hängen
    [sheet, stickyEl, scrimGlobal].forEach(node => {
      if (node && node.parentNode !== document.body) document.body.appendChild(node);
    });
    
    // Body-Lock & Inert
    const setSiblingsInert = (on) => {
      const main = document.getElementById(Config.SELECTORS.MAIN_CONTENT.replace('#', ''));
      const header = document.querySelector(Config.SELECTORS.HEADER);
      if (main) {
        if (on) {
          main.setAttribute('inert', '');
          main.setAttribute('aria-hidden', 'true');
          main.style.pointerEvents = 'none';
        } else {
          main.removeAttribute('inert');
          main.removeAttribute('aria-hidden');
          main.style.pointerEvents = '';
        }
      }
      if (header) header.style.pointerEvents = '';
    };
    
    const lockBodyScroll = () => {
      scrollY = window.scrollY || document.documentElement.scrollTop || 0;
      document.body.setAttribute(Config.DATA_ATTRS.BB_SCROLL_Y, String(scrollY));
      document.documentElement.classList.add(Config.CLASSES.LOCK);
      document.body.classList.add(Config.CLASSES.LOCK);
    };
    
    const unlockBodyScroll = () => {
      const y = Number(document.body.getAttribute(Config.DATA_ATTRS.BB_SCROLL_Y) || scrollY || 0);
      const html = document.documentElement;
      const prev = html.style.scrollBehavior;
      html.style.scrollBehavior = 'auto';
      document.documentElement.classList.remove(Config.CLASSES.LOCK);
      document.body.classList.remove(Config.CLASSES.LOCK);
      window.scrollTo(0, y);
      html.style.scrollBehavior = prev || '';
      document.body.removeAttribute(Config.DATA_ATTRS.BB_SCROLL_Y);
    };
    
    // Freeze/Unfreeze Header
    const freezeHeaderWhileLocked = () => {
      const header = document.querySelector('sticky-header') ||
                     document.querySelector('.header-wrapper') ||
                     document.querySelector('header[role="banner"]') ||
                     document.querySelector(Config.SELECTORS.HEADER);
      if (!header) return;
      const hr = header.getBoundingClientRect();
      const innerHeader = header.querySelector('.header') || header;
      if (innerHeader) {
        const cs = getComputedStyle(innerHeader);
        document.documentElement.style.setProperty(Config.CSS_VARS.PAD_LEFT, cs.paddingLeft);
        document.documentElement.style.setProperty(Config.CSS_VARS.PAD_RIGHT, cs.paddingRight);
        innerHeader.style.paddingLeft = cs.paddingLeft;
        innerHeader.style.paddingRight = cs.paddingRight;
      }
      const headerRect = (innerHeader || header).getBoundingClientRect();
      document.documentElement.classList.add(Config.CLASSES.FREEZE_HEADER);
      const logo = header.querySelector(Config.SELECTORS.HEADER_LOGO);
      if (logo) {
        const lr = logo.getBoundingClientRect();
        logo.style.width = `${lr.width}px`;
        logo.style.height = `${lr.height}px`;
        logo.style.maxWidth = `${lr.width}px`;
        document.documentElement.style.setProperty(Config.CSS_VARS.LOGO_W, `${lr.width}px`);
        document.documentElement.style.setProperty(Config.CSS_VARS.LOGO_H, `${lr.height}px`);
        const cx = (lr.left - headerRect.left) + (lr.width / 2);
        document.documentElement.style.setProperty(Config.CSS_VARS.LOGO_X, `${Math.round(cx)}px`);
      }
      const heading = header.querySelector(Config.SELECTORS.HEADER_HEADING);
      if (heading) {
        header.style.position = header.style.position || 'relative';
        
        // Context7: SOT - Only save original values if not already saved
        // This prevents overwriting with incorrect values from previous sheet opens
        // Root Cause Fix: If heading.style.left already has a value (from previous unfreeze),
        // we must NOT overwrite the original value stored in BB_PREV_LEFT
        if (!heading.hasAttribute(Config.DATA_ATTRS.BB_PREV_LEFT)) {
          // First time: Save the TRUE original values (before any sheet opens)
          const computedStyle = getComputedStyle(heading);
          heading.setAttribute(Config.DATA_ATTRS.BB_PREV_POS, heading.style.position || computedStyle.position || '');
          heading.setAttribute(Config.DATA_ATTRS.BB_PREV_LEFT, heading.style.left || computedStyle.left || '');
          heading.setAttribute(Config.DATA_ATTRS.BB_PREV_RIGHT, heading.style.right || computedStyle.right || '');
          heading.setAttribute(Config.DATA_ATTRS.BB_PREV_TRANSFORM, heading.style.transform || computedStyle.transform || '');
          heading.setAttribute(Config.DATA_ATTRS.BB_PREV_MARGIN, heading.style.margin || computedStyle.margin || '');
        }
        // Subsequent opens: Use already-saved original values, don't overwrite
        
        heading.style.position = 'absolute';
        heading.style.left = getComputedStyle(document.documentElement).getPropertyValue(Config.CSS_VARS.LOGO_X).trim() || '50%';
        heading.style.right = 'auto';
        heading.style.transform = 'translateX(-50%)';
        heading.style.margin = '0';
      }
      document.documentElement.style.setProperty(Config.CSS_VARS.HEADER_H, `${hr.height}px`);
      const wrapperHost = document.querySelector('.header-wrapper') || document.querySelector('sticky-header');
      if (wrapperHost) {
        wrapperHost.style.minHeight = `${hr.height}px`;
        wrapperHost.style.willChange = 'auto';
      }
    };
    
    const unfreezeHeader = () => {
      document.documentElement.classList.remove(Config.CLASSES.FREEZE_HEADER);
      document.documentElement.style.removeProperty(Config.CSS_VARS.HEADER_H);
      document.documentElement.style.removeProperty(Config.CSS_VARS.LOGO_W);
      document.documentElement.style.removeProperty(Config.CSS_VARS.LOGO_H);
      document.documentElement.style.removeProperty(Config.CSS_VARS.LOGO_X);
      const header = document.querySelector('sticky-header') ||
                     document.querySelector('.header-wrapper') ||
                     document.querySelector('header[role="banner"]') ||
                     document.querySelector(Config.SELECTORS.HEADER);
      const logo = header?.querySelector(Config.SELECTORS.HEADER_LOGO);
      if (logo) {
        logo.style.width = '';
        logo.style.height = '';
        logo.style.maxWidth = '';
      }
      const heading = header?.querySelector(Config.SELECTORS.HEADER_HEADING);
      if (heading) {
        // Context7: SOT - Restore original values from data attributes
        const prevPos = heading.getAttribute(Config.DATA_ATTRS.BB_PREV_POS) || '';
        const prevLeft = heading.getAttribute(Config.DATA_ATTRS.BB_PREV_LEFT) || '';
        const prevRight = heading.getAttribute(Config.DATA_ATTRS.BB_PREV_RIGHT) || '';
        const prevTransform = heading.getAttribute(Config.DATA_ATTRS.BB_PREV_TRANSFORM) || '';
        const prevMargin = heading.getAttribute(Config.DATA_ATTRS.BB_PREV_MARGIN) || '';
        
        heading.style.position = prevPos;
        // Context7: SOT - Explicitly remove inline style if value is empty
        // This ensures heading.style.left is completely cleared, preventing accumulation of wrong values
        if (prevLeft) {
          heading.style.left = prevLeft;
        } else {
          heading.style.removeProperty('left');
        }
        
        if (prevRight) {
          heading.style.right = prevRight;
        } else {
          heading.style.removeProperty('right');
        }
        
        heading.style.transform = prevTransform;
        heading.style.margin = prevMargin;
        
        // Clean up data attributes
        heading.removeAttribute(Config.DATA_ATTRS.BB_PREV_POS);
        heading.removeAttribute(Config.DATA_ATTRS.BB_PREV_LEFT);
        heading.removeAttribute(Config.DATA_ATTRS.BB_PREV_RIGHT);
        heading.removeAttribute(Config.DATA_ATTRS.BB_PREV_TRANSFORM);
        heading.removeAttribute(Config.DATA_ATTRS.BB_PREV_MARGIN);
      }
      const wrapper = document.querySelector('.header-wrapper') || document.querySelector('sticky-header');
      if (wrapper) {
        wrapper.style.minHeight = '';
        wrapper.style.willChange = '';
        document.documentElement.style.removeProperty(Config.CSS_VARS.PAD_LEFT);
        document.documentElement.style.removeProperty(Config.CSS_VARS.PAD_RIGHT);
        const hdr = document.querySelector('sticky-header') ||
                    document.querySelector('.header-wrapper') ||
                    document.querySelector('header[role="banner"]') ||
                    document.querySelector(Config.SELECTORS.HEADER);
        const inner = hdr?.querySelector('.header') || hdr;
        if (inner) {
          inner.style.paddingLeft = '';
          inner.style.paddingRight = '';
        }
      }
    };
    
    // Mobile Loading Overlay
    const bbMobileLoadingShow = (text) => {
      UIRenderMobile.showMobileLoading();
    };
    const bbMobileLoadingHide = () => {
      UIRenderMobile.hideMobileLoading();
    };
    
    // Sheet Summary Functions
    const mobile_updateSheetSummary = () => {
      if (!Utils.isMobile()) return;
      const src = sheetItems || state.items;
      const q = Object.values(src).reduce((a,b)=>a+b,0);
      const countEl = sheet?.querySelector(Config.SELECTORS.SHEET_COUNT);
      const nextEl = sheet?.querySelector(Config.SELECTORS.SHEET_NEXT);
      if (countEl) countEl.textContent = `${q}/${maxSlots}`;
      const next = tiers.find(t => t.qty > q);
      if (nextEl) {
        if (!q && next) {
          nextEl.textContent = `noch ${next.qty - q} bis −${next.off}%`;
        } else if (next) {
          nextEl.textContent = `noch ${next.qty - q} bis −${next.off}%`;
        } else {
          nextEl.textContent = Config.TEXTS.TIER_MAX;
        }
      }
    };
    
    const mobile_updateSheetQuantities = () => {
      if (!Utils.isMobile()) return;
      const src = sheetItems || state.items;
      UIRenderMobile.updateSheetQuantities(src, sheet);
    };
    
    const updateMilestoneChips = (qOverride) => {
      if (!Utils.isMobile()) return;
      const q = (typeof qOverride === 'number') ? qOverride : state.totalQty;
      UIRenderMobile.updateMilestoneChips(q, state, tiers, sheet);
    };
    
    const mobile_applySlotNumbers = () => {
      if (!Utils.isMobile()) return;
      const slots = Array.from(document.querySelectorAll(Config.SELECTORS.SLOTS));
      UIRenderMobile.applyMobileSlotLabels(slots, tiers);
    };
    
    const mobile_fixSlotAria = () => {
      if (!Utils.isMobile()) return;
      document.querySelectorAll(Config.SELECTORS.SLOTS).forEach(slot => {
        const i = slot.dataset.slot;
        slot.setAttribute('aria-label', `Slot ${i}`);
      });
    };
    
    // Bottom-Sheet Open/Close
    const openSheet = async () => {
      if (!Utils.isMobile() || !sheet || sheetOpen) return;
      
      // IMMER frischen State holen (verhindert Stale State)
      sheetItems = JSON.parse(JSON.stringify(state.items || {}));
      
      // Prüfen ob Cart leer ist
      try {
        const cart = await API.fetchCart();
        const items = cart.items || [];
        if (!items.length) {
          sheetItems = {};
          state.reset();
        } else {
          const groups = await API.readAllBundleGroupsOfProduct(productId);
          const hasAnyOfThisProduct = [...groups.values()].some(g => g.totalQty > 0);
          if (!hasAnyOfThisProduct) {
            sheetItems = {};
            state.reset();
          } else {
            const grp = await API.loadBundleFromCart(productId, state, state.activeBundleKey);
            if (grp) {
              sheetItems = JSON.parse(JSON.stringify(state.items || {}));
            }
          }
        }
      } catch (error) {
        console.warn('[Bundle Builder] Error checking cart state before opening sheet:', error);
      }
      
      // Context7: SOT - Protection Mechanism for Header Measurement
      // Hide ALL sticky bars BEFORE measuring header/logo position
      // This prevents sticky bar height from affecting getBoundingClientRect() calculations
      // Critical for correct logo centering on ALL entry points (PDP Normal, LP, PDP Sticky)
      const bbStickyEl = document.getElementById('bb-sticky');
      const stickyBuymeEl = document.getElementById('sticky-buyme');
      if (bbStickyEl) {
        bbStickyEl.classList.remove(Config.CLASSES.VISIBLE);
        bbStickyEl.setAttribute('aria-hidden', 'true');
      }
      if (stickyBuymeEl) {
        stickyBuymeEl.classList.remove('is-visible');
        stickyBuymeEl.setAttribute('aria-hidden', 'true');
      }
      
      lockBodyScroll();
      
      // Context7: SOT - Logo-Recalc MUST run BEFORE freezeHeaderWhileLocked()
      // freezeHeaderWhileLocked() uses LOGO_X to position the heading, so LOGO_X must be set first
      // Now that sticky bars are hidden, getBoundingClientRect() returns correct values
      const bbRecalcLogoCenter = () => {
        const header = document.querySelector('sticky-header') ||
                       document.querySelector('.header-wrapper') ||
                       document.querySelector('header[role="banner"]') ||
                       document.querySelector(Config.SELECTORS.HEADER);
        const inner = header?.querySelector('.header') || header;
        const logo = header?.querySelector(Config.SELECTORS.HEADER_LOGO);
        if (!inner || !logo) return;
        const hr = inner.getBoundingClientRect();
        const lr = logo.getBoundingClientRect();
        const cx = (lr.left - hr.left) + (lr.width / 2);
        const logoX = `${Math.round(cx)}px`;
        document.documentElement.style.setProperty(Config.CSS_VARS.LOGO_X, logoX);
      };
      bbRecalcLogoCenter();
      
      freezeHeaderWhileLocked();
      setSiblingsInert(true);
      
      sheet.removeAttribute('hidden');
      
      // AUDIT FIX: requestIdleCallback fallback for image preloading
      const preloadImages = () => {
        UIRenderMobile.preloadSheetResources(sheet);
        // Additional eager loading for first 8 images
        (function mobile_prewarmSheetImages(limit = 8) {
          const imgs = sheet.querySelectorAll('.bb-sheet__grid .bb-tile__imgwrap img');
          let n = 0;
          imgs.forEach(img => {
            if (n >= limit) return;
            img.loading = 'eager';
            if (img.decode) {
              try { img.decode(); } catch(e) {}
            }
            n++;
          });
        })();
      };
      
      if ('requestIdleCallback' in window) {
        requestIdleCallback(preloadImages, { timeout: 2000 });
      } else {
        setTimeout(preloadImages, 1000);
      }
      
      sheet.classList.add(Config.CLASSES.OPEN);
      sheetOpen = true;
      Analytics.trackSheetOpen({ source: 'cta' });
      sheetPrevFocus = document.activeElement;
      panel?.setAttribute('tabindex', '-1');
      panel?.focus({ preventScroll: true });
      
      document.documentElement.classList.add(Config.CLASSES.SHEET_OPEN);
      document.querySelector('.header .header__heading')?.removeAttribute('aria-hidden');
      
      // Context7: SOT - Logo-Recalc already done before freezeHeaderWhileLocked()
      // Schedule one more recalc after sheet animation to catch any layout changes
      requestAnimationFrame(() => {
        bbRecalcLogoCenter();
      });
      
      stickyEl?.classList.remove(Config.CLASSES.VISIBLE);
      scrimGlobal?.removeAttribute('hidden');
      
      // Update Sheet UI
      mobile_updateSheetQuantities();
      mobile_updateSheetSummary();
      updateMilestoneChips(Object.values(sheetItems).reduce((a,b)=>a+b,0));
      
      // Update main UI
      if (updateUI) updateUI();
    };
    
    const closeSheetAsync = () => {
      if (!sheetOpen || !sheet) return Promise.resolve();
      sheet.classList.remove(Config.CLASSES.OPEN);
      sheet.classList.add(Config.CLASSES.CLOSING);
      sheetOpen = false;
      
      return new Promise(resolve => {
        const finish = () => {
          sheet.setAttribute('hidden', '');
          sheet.classList.remove(Config.CLASSES.CLOSING);
          setTimeout(() => scrimGlobal?.setAttribute('hidden', ''), Config.CONSTANTS.SCRIM_HIDE_DELAY_MS);
          unfreezeHeader();
          unlockBodyScroll();
          setSiblingsInert(false);
          if (sheetPrevFocus && sheetPrevFocus.focus) sheetPrevFocus.focus();
          sheetItems = null;
          mobile_gateSticky();
          Analytics.trackSheetClose({ totalQty: state.totalQty });
          document.documentElement.classList.remove(Config.CLASSES.SHEET_OPEN);
          resolve();
        };
        const t = setTimeout(finish, Config.CONSTANTS.CLOSE_ANIMATION_TIMEOUT_MS);
        panel?.addEventListener('animationend', () => { clearTimeout(t); finish(); }, { once: true });
      });
    };
    
    const closeSheet = async () => {
      await closeSheetAsync();
    };
    
    // Focus-Trap
    const focusablesSelector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const trapFocus = (e) => {
      if (!sheetOpen) return;
      if (e.key !== 'Tab') return;
      const nodes = panel.querySelectorAll(focusablesSelector);
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        last.focus();
        e.preventDefault();
      } else if (!e.shiftKey && document.activeElement === last) {
        first.focus();
        e.preventDefault();
      }
    };
    
    // AUDIT FIX: Use AbortController for focus trap
    panel?.addEventListener('keydown', trapFocus, { signal });
    
    // Logo-Center-Recalc bei Resize/Rotation
    const bbRecalcLogoCenterIfOpen = () => {
      if (!sheetOpen) return;
      const header = document.querySelector('sticky-header') ||
                     document.querySelector('.header-wrapper') ||
                     document.querySelector('header[role="banner"]') ||
                     document.querySelector(Config.SELECTORS.HEADER);
      const inner = header?.querySelector('.header') || header;
      const logo = header?.querySelector(Config.SELECTORS.HEADER_LOGO);
      if (!inner || !logo) return;
      const hr = inner.getBoundingClientRect();
      const lr = logo.getBoundingClientRect();
      const cx = (lr.left - hr.left) + (lr.width / 2);
      const logoX = `${Math.round(cx)}px`;
      document.documentElement.style.setProperty(Config.CSS_VARS.LOGO_X, logoX);
    };
    
    // AUDIT FIX: Use AbortController for resize/orientation listeners
    window.addEventListener('resize', bbRecalcLogoCenterIfOpen, { passive: true, signal });
    window.addEventListener('orientationchange', () => setTimeout(bbRecalcLogoCenterIfOpen, Config.CONSTANTS.LOGO_RECALC_DELAY_MS), { passive: true, signal });
    
    // VisualViewport (iOS/Android)
    if (window.visualViewport) {
      const vvRecalc = () => bbRecalcLogoCenterIfOpen();
      // AUDIT FIX: Use AbortController for visualViewport listeners
      visualViewport.addEventListener('resize', vvRecalc, { passive: true, signal });
      visualViewport.addEventListener('scroll', vvRecalc, { passive: true, signal });
    }
    
    // Sheet Event-Delegation
    if (sheet) {
      if (!sheet.dataset[Config.DATA_ATTRS.BB_BUTTONS_ATTACHED.replace('data-', '').replace('-', '')]) {
        sheet.setAttribute(Config.DATA_ATTRS.BB_BUTTONS_ATTACHED, 'true');
        // AUDIT FIX: Use AbortController for sheet click handler
        sheet.addEventListener('click', (e) => {
          const closeBtn = e.target.closest(Config.SELECTORS.CLOSE);
          if (closeBtn) {
            e.preventDefault();
            e.stopPropagation();
            closeSheet();
            return;
          }
          const resetBtn = e.target.closest(Config.SELECTORS.RESET);
          if (resetBtn) {
            e.preventDefault();
            e.stopPropagation();
            sheetItems = {};
            Analytics.trackReset();
            mobile_updateSheetQuantities();
            mobile_updateSheetSummary();
            updateMilestoneChips(0);
            return;
          }
          const commitBtn = e.target.closest(Config.SELECTORS.COMMIT);
          if (commitBtn) {
            e.preventDefault();
            e.stopPropagation();
            handleCommit();
            return;
          }
        }, { signal });
      }
    }
    
    // Stepper Delegation
    if (sheet) {
      if (!sheet.dataset[Config.DATA_ATTRS.BB_STEPPER_ATTACHED.replace('data-', '').replace('-', '')]) {
        sheet.setAttribute(Config.DATA_ATTRS.BB_STEPPER_ATTACHED, 'true');
        // AUDIT FIX: Use AbortController for stepper click handler
        sheet.addEventListener('click', (e) => {
          if (!Utils.isMobile() || !sheetOpen) return;
          
          const addBtn = e.target.closest(Config.SELECTORS.BTN_ADD);
          if (addBtn) {
            const tile = addBtn.closest(Config.SELECTORS.TILE);
            if (tile) {
              const plusBtn = tile.querySelector(Config.SELECTORS.STEPPER_PLUS);
              if (plusBtn) {
                plusBtn.click();
                return;
              }
            }
          }
          
          const plus = e.target.closest(Config.SELECTORS.STEPPER_PLUS);
          const minus = e.target.closest(Config.SELECTORS.STEPPER_MINUS);
          if (!plus && !minus) return;
          const tile = e.target.closest(Config.SELECTORS.TILE);
          if (!tile) return;
          const vid = String(tile.dataset.variantId);
          
          if (!sheetItems) sheetItems = JSON.parse(JSON.stringify(state.items || {}));
          const cur = sheetItems[vid] || 0;
          const draftTotal = Object.values(sheetItems).reduce((a,b)=>a+b,0);
          
          if (plus) {
            if (draftTotal < maxSlots) sheetItems[vid] = cur + 1;
          } else {
            if (cur <= 1) delete sheetItems[vid];
            else sheetItems[vid] = cur - 1;
          }
          
          const totalSheetQty = Object.values(sheetItems).reduce((a,b)=>a+b,0);
          if (totalSheetQty > maxSlots) {
            console.warn('[Bundle Builder] Mobile stepper: Total quantity exceeds maximum, capping');
            sheetItems = Utils.capToMaximum(sheetItems, maxSlots);
          }
          
          mobile_updateSheetQuantities();
          mobile_updateSheetSummary();
          updateMilestoneChips(Object.values(sheetItems).reduce((a,b)=>a+b,0));
          Analytics.trackQtyChange(vid, plus ? 1 : -1, sheetItems[vid] || 0);
        }, { signal });
      }
    }
    
    // Commit Handler
    const handleCommit = async () => {
      if (!Utils.isMobile()) return;
      const t0 = performance.now();
      
      if (sheetItems) {
        const totalSheetQty = Object.values(sheetItems).reduce((a,b)=>a+b,0);
        if (totalSheetQty > maxSlots) {
          console.warn('[Bundle Builder] Mobile commit: Total quantity exceeds maximum, capping to', maxSlots);
          const cappedSheetItems = Utils.capToMaximum(sheetItems, maxSlots);
          state.setItems(cappedSheetItems);
        } else {
          state.setItems(sheetItems);
        }
        if (markDirty) markDirty();
        if (updateUI) updateUI();
        Analytics.trackCommit(state.totalQty, state.tier ? state.tier.off : 0);
      }
      
      const cartEl = document.querySelector(Config.SELECTORS.CART_NOTIFICATION) || 
                     document.querySelector(Config.SELECTORS.CART_DRAWER);
      const wasOpenBefore = !!cartEl?.hasAttribute?.('open');
      
      const syncP = syncCartBundleToState();
      await closeSheetAsync();
      bbMobileLoadingShow(Config.TEXTS.LOADING_TEXT);
      const MIN_MS = Config.CONSTANTS.MIN_LOADING_MS;
      const minP = Utils.bbDelay(MIN_MS);
      await syncP;
      const elapsed = performance.now() - t0;
      const userClosed = getUserClosedDuringSync ? getUserClosedDuringSync() : false;
      if (openCartAfterAdd && elapsed <= FAST_OPEN_MS && !wasOpenBefore && !userClosed) {
        // Enhanced safety check: Verify drawer exists in DOM and is fully initialized
        const drawerExists = cartEl && document.contains(cartEl);
        const drawerOverlay = document.querySelector('#CartDrawer-Overlay');
        const isDrawerReady = drawerExists && drawerOverlay && typeof cartEl.open === 'function';
        
        if (isDrawerReady) {
          cartEl.open();
        } else {
          // Fallback: Show success toast if drawer not available or not ready
          // Context7: Platform Separation - showToast is a utility function (platform-agnostic)
          const UIRenderShared = window.EdumonBundle?.UIRenderShared;
          if (UIRenderShared && typeof UIRenderShared.showToast === 'function') {
            UIRenderShared.showToast(
              Config.TEXTS.SUCCESS_ADD_CART || 'Warenkorb aktualisiert',
              'success'
            );
          } else {
            // Fallback for backward compatibility
            const UIRenderDesktop = window.EdumonBundle?.UIRenderDesktop;
            if (UIRenderDesktop && typeof UIRenderDesktop.showToast === 'function') {
              UIRenderDesktop.showToast(
                Config.TEXTS.SUCCESS_ADD_CART || 'Warenkorb aktualisiert',
                'success'
              );
            }
          }
          
          // Try to update cart bubble count if available
          const cartBubble = document.querySelector('[data-cart-count]') || 
                             document.querySelector('.cart-count-bubble');
          if (cartBubble) {
            // Trigger cart refresh event for bubble update
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          }
        }
      }
      await minP;
      bbMobileLoadingHide();
    };
    
    // Express Bundle
    // AUDIT FIX: Use AbortController for express bundle click
    document.querySelector(Config.SELECTORS.EXPRESS_BUNDLE)?.addEventListener('click', async (e) => {
      e.preventDefault();
      if (typeof sheetItems !== 'undefined' && sheetItems) {
        state.setItems(sheetItems);
        if (markDirty) markDirty();
        if (updateUI) updateUI();
      }
      await syncCartBundleToState();
      window.location.assign('/checkout');
    }, { signal });
    
    // "Booster wählen" Button Handler
    // AUDIT FIX: Use AbortController for CTA click
    ctaBtn?.addEventListener('click', (e) => {
      if (!Utils.isMobile()) return;
      e.preventDefault();
      openSheet();
    }, { signal });
    
    // AUDIT FIX: Use AbortController for sticky open click
    stickyEl?.querySelector(Config.SELECTORS.OPEN)?.addEventListener('click', (e) => {
      if (!Utils.isMobile()) return;
      e.preventDefault();
      openSheet();
    }, { signal });
    
    // Globaler Scrim klickbar
    // AUDIT FIX: Use AbortController for scrim click
    scrimGlobal?.addEventListener('click', (e) => {
      e.preventDefault();
      closeSheet();
    }, { signal });
    
    // ESC key handler
    // AUDIT FIX: Use AbortController for ESC key handler
    const escHandler = (e) => {
      if (e.key === 'Escape' && sheetOpen) closeSheet();
    };
    document.addEventListener('keydown', escHandler, { signal });
    
    // Global Event Listener
    // AUDIT FIX: Use AbortController for edm:activate-bundle event
    document.addEventListener('edm:activate-bundle', (e) => {
      if (!sheetOpen) {
        openSheet();
      } else {
        const panel = document.querySelector(Config.SELECTORS.SHEET_PANEL);
        if (panel) {
          panel.classList.add('bb--pulse');
          setTimeout(() => panel.classList.remove('bb--pulse'), 300);
        }
      }
    }, { signal });
    
    // Auto-Open Logic
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('trigger_sheet') === 'true') {
      setTimeout(() => {
        if (!sheetOpen) {
          openSheet();
          try {
            const newUrl = window.location.protocol + "//" + window.location.host + window.location.pathname;
            window.history.replaceState({path: newUrl}, '', newUrl);
          } catch(e) {}
        }
      }, 300);
    }
    
    // AUDIT FIX: MediaQuery listener with cleanup
    mqMobile = Utils.getMobileMQ();
    const handleMobileChange = (e) => {
      if (e.matches) document.body.classList.add(Config.CLASSES.MOBILE_ACTIVE);
      else document.body.classList.remove(Config.CLASSES.MOBILE_ACTIVE);
      
      if (e.matches) {
        mobile_buildTicks();
        mobile_applySlotNumbers();
        mobile_updateSticky();
        mobile_gateSticky();
        mobile_fixSlotAria();
      } else {
        stickyEl?.classList.remove(Config.CLASSES.VISIBLE);
      }
    };
    
    if (mqMobile.addEventListener) {
      mqMobile.addEventListener('change', handleMobileChange);
      mqMobileListener = handleMobileChange;
    } else if (mqMobile.addListener) {
      mqMobile.addListener(handleMobileChange);
      mqMobileListener = handleMobileChange;
    }
    
    // Initialisierung Mobile
    if (Utils.isMobile()) {
      mobile_buildTicks();
      mobile_applySlotNumbers();
      mobile_updateSticky();
      mobile_gateSticky();
      mobile_fixSlotAria();
    }
    
    // Return manager instance
    return {
      // Public API
      openSheet,
      closeSheet,
      updateSticky: mobile_updateSticky,
      updateSheetSummary: mobile_updateSheetSummary,
      updateSheetQuantities: mobile_updateSheetQuantities,
      updateMilestoneChips,
      buildTicks: mobile_buildTicks,
      applySlotNumbers: mobile_applySlotNumbers,
      fixSlotAria: mobile_fixSlotAria,
      gateSticky: mobile_gateSticky,
      showLoading: bbMobileLoadingShow,
      hideLoading: bbMobileLoadingHide,
      getSheetItems: () => sheetItems,
      setSheetItems: (items) => { sheetItems = items; },
      isSheetOpen: () => sheetOpen,
      
      // AUDIT FIX: Cleanup method
      destroy: function() {
        // Abort all event listeners
        abortController.abort();
        
        // Disconnect IntersectionObserver
        if (ioAnchor) {
          try {
            ioAnchor.disconnect();
          } catch(e) {}
          ioAnchor = null;
        }
        
        // Remove MediaQuery listener
        if (mqMobile && mqMobileListener) {
          if (mqMobile.removeEventListener) {
            mqMobile.removeEventListener('change', mqMobileListener);
          } else if (mqMobile.removeListener) {
            mqMobile.removeListener(mqMobileListener);
          }
          mqMobileListener = null;
        }
        
        // Clear timers
        if (bbDebounceTimer) {
          clearTimeout(bbDebounceTimer);
          bbDebounceTimer = null;
        }
      }
    };
  }
};
